import quickFetch from 'quick-fetch';
import { getChirpEarlyRequestName } from 'rpc-client-utils';
export const handleEarlyRequestError = err => {
  // eslint-disable-next-line no-console
  console.warn('[early-request] CHIRP early request failed:', err);
};
export const getEarlyRequestResult = (name, fallbackFetcher) => {
  const earlyRequest = name && quickFetch.getRequestStateByName(name);
  if (!earlyRequest) {
    if (!fallbackFetcher) {
      throw new Error('No fallback fetcher provided');
    }
    return fallbackFetcher();
  }
  return new Promise((resolve, reject) => {
    earlyRequest.whenFinished(result => {
      quickFetch.removeEarlyRequest(name);
      resolve(result);
    });
    earlyRequest.onError((__xhr, errorMessage) => {
      quickFetch.removeEarlyRequest(name);
      reject(new Error(errorMessage));
    });
  });
};

/**
 * Gets a CHIRP early request result, or falls back to making the RPC call.
 *
 * IMPORTANT: The details and request object must EXACTLY match what was passed to
 * makeChirpEarlyRequest() in the early requester, or the hash won't match.
 *
 * @param details - The RPC details object with { ...rpc, earlyRequest: true }
 * @param requestObject - The request parameters { request: {...} }
 * @param fallbackFetcher - Function to call if early request not found
 */
export const getChirpEarlyRequest = ({
  details,
  requestObject,
  fallbackFetcher
}) => {
  // Compute the early request name using the same algorithm as makeChirpEarlyRequest
  const requestName = getChirpEarlyRequestName(details, requestObject);
  const requestState = quickFetch.getRequestStateByName(requestName);
  if (requestState && !requestState.error) {
    return new Promise((resolve, reject) => {
      requestState.whenFinished(response => {
        quickFetch.removeEarlyRequest(requestName);
        try {
          const parsed = JSON.parse(response);
          if (typeof parsed === 'object' && parsed !== null && (parsed.type === 'rpcError' || parsed.type === 'internalError')) {
            // Early request returned a CHIRP error envelope as a successful
            // HTTP response (e.g. an early-request hitting the wrong gateway
            // and getting a SERVICE_NOT_FOUND internalError). Treat it the
            // same as a rejected early request: fall back to a fresh RPC
            // call so callers receive a real success payload instead of an
            // error shape masquerading as data.
            fallbackFetcher().then(resolve).catch(reject);
            return;
          }
          resolve(parsed.data || parsed);
        } catch (e) {
          // Truncated response (e.g. network interruption mid-stream).
          // Fall back to a fresh RPC call instead of failing the page.
          fallbackFetcher().then(resolve).catch(reject);
        }
      });
      requestState.onError((__xhr, __errorMessage) => {
        quickFetch.removeEarlyRequest(requestName);
        // Any early-request error (network, gateway, parse failure) should
        // fall back to a fresh RPC call so callers can recover. Resolving
        // with the parsed error envelope was previously masking failures by
        // handing error shapes to success-path consumers.
        fallbackFetcher().then(resolve).catch(reject);
      });
    });
  }
  return fallbackFetcher();
};